import {
  CloseCircleOutlined,
  CloseOutlined,
  CompressOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExpandOutlined,
  FileImageOutlined,
  LoadingOutlined,
  PictureOutlined,
  PlusOutlined,
  RedoOutlined,
  ZoomInOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { Avatar, Button, Image, Input, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { VistoeraActionConfirm } from '../ui/action'
import type { TextAreaRef } from 'antd/es/input/TextArea'
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type ImgHTMLAttributes } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { triggerBrowserDownload } from '../../api/client'
import {
  aiChatAssetContentUrl,
  deleteAIChatConversation,
  getAIChatConfig,
  getAIChatConversation,
  listAIChatConversations,
  resolveImagePickerSelection,
  retryAIChatExecution,
  sendAIChatMessage,
  siteAssetVariantUrl,
  uploadSiteAsset,
  type AIChatConfig,
  type AIChatConversationDetail,
  type AIChatConversationSummary,
  type AIChatExecution,
  type AIChatExecutionStatus,
  type AIChatMessage,
  type AIChatOutputAsset,
  type AIChatProvider,
  type ImageAssetPickerSelection,
} from '../api'
import { ImageAssetPicker } from '../components/ImageAssetPicker'
import { lockPageScroll, unlockPageScroll } from '../utils/scrollLock'
import { formatDateTime as formatDateTimeI18n } from '../../i18n/dateFormat'
import { useUiLocale } from '../../i18n/LocaleProvider'
import { useWorkspaceState } from './WorkspaceContext'
import { hasRichResponse, type RichResponseV1 } from './aiChatRichResponse'
import { RichResponseRenderer } from './RichResponseRenderer'
import './ai-chat.css'

const POLL_MS = 2500
const ELAPSED_TICK_MS = 1000
const FALLBACK_MAX_IMAGES = 4
const FALLBACK_PROMPT_MAX = 4000
// Result UX Fix P0 (2026-08-26) — how long the tenant UI will keep showing
// "结果处理中" after the backend already reports `execution.status ===
// "succeeded"` but no renderable result (text/images) has propagated yet,
// before giving up and showing an explicit "结果暂未就绪" message instead of
// spinning forever (spec §21/§22: never a fixed `setTimeout` that fakes
// completion — this only ever *stops pretending it's still loading*; the
// judgement of what to render always comes from live execution/asset state,
// see `resolveTenantResultState`). 60s, not 5s: this is the same class of
// race the AI Chat Live Output Fix P0 already found once between Worker
// `/complete` and `AIChatExecutionOutputAsset` persistence — long enough to
// absorb a slow image upload/registration, short enough to stop lying to
// the tenant once it's genuinely stuck.
const RESULT_PROPAGATION_TIMEOUT_MS = 60000
// Background polling itself keeps trying for a while past the point the UI
// already gives up and shows the timeout message (self-heals if the result
// really does show up late) — but not forever, so a permanently-stuck
// execution doesn't poll this tenant's browser indefinitely.
const POLL_HARD_STOP_MS = RESULT_PROPAGATION_TIMEOUT_MS * 3

const PROVIDER_ORDER: AIChatProvider[] = ['chatgpt', 'gemini', 'grok']
const PROVIDER_FALLBACK_LABEL: Record<string, string> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  grok: 'Grok',
}
const PROVIDER_AVATAR_COLOR: Record<string, string> = {
  chatgpt: '#16a34a',
  gemini: '#2563eb',
  grok: '#6366f1',
  qwen: '#7c3aed',
  deepseek: '#1d4ed8',
}

const TERMINAL_STATUSES = new Set<AIChatExecutionStatus>(['succeeded', 'failed', 'cancelled', 'interrupted'])

// ---------------------------------------------------------------------
// Tenant Result Readiness (spec §17-§26) — `execution.status ===
// "succeeded"` means the Worker/backend finished; it does NOT by itself
// mean the tenant has anything renderable yet (text/images can still be
// propagating). These two shared helpers are the ONE place that decision
// is made — every ProviderCard/status badge/polling gate reads through
// them instead of each re-deriving its own "is this done" logic.
// ---------------------------------------------------------------------
function hasRenderableResult(execution: AIChatExecution | null | undefined): boolean {
  if (!execution) return false
  const hasText = (execution.response_text || '').trim().length > 0
  const hasAssets = (execution.output_assets?.length ?? 0) > 0
  const hasRich = hasRichResponse(execution.rich_response as RichResponseV1 | null | undefined)
  return hasText || hasAssets || hasRich
}

type TenantResultState =
  | 'idle'
  | 'queued'
  | 'running'
  | 'result_processing'
  | 'ready'
  | 'result_timeout'
  | 'failed'
  | 'cancelled'

/** `isResultReady(state) === (state === 'ready')` — kept as a named
 * predicate (spec §26) so call sites read as intent ("is this ready to
 * show?"), not as a magic string comparison. */
function isResultReadyState(state: TenantResultState): boolean {
  return state === 'ready'
}

function resolveTenantResultState(execution: AIChatExecution | null | undefined, nowMs: number): TenantResultState {
  if (!execution) return 'idle'
  if (execution.status === 'queued') return 'queued'
  if (execution.status === 'running') return 'running'
  if (execution.status === 'failed') return 'failed'
  if (execution.status === 'cancelled' || execution.status === 'interrupted') return 'cancelled'
  // status === 'succeeded' from here on — this is exactly the
  // EXECUTION_SUCCEEDED-vs-TENANT_RESULT_READY split (spec §17).
  if (hasRenderableResult(execution)) return 'ready'
  const completedAt = execution.completed_at ? new Date(execution.completed_at).getTime() : null
  if (completedAt != null && !Number.isNaN(completedAt) && nowMs - completedAt > RESULT_PROPAGATION_TIMEOUT_MS) {
    return 'result_timeout'
  }
  return 'result_processing'
}

const RESULT_STATE_BADGE_CLASS: Record<TenantResultState, string> = {
  idle: '',
  queued: 'tc-badge-warning',
  running: 'tc-badge-info',
  result_processing: 'tc-badge-info',
  ready: 'tc-badge-success',
  result_timeout: 'tc-badge-error',
  failed: 'tc-badge-error',
  cancelled: 'tc-badge-neutral',
}

function resultStateLabel(state: TenantResultState, t: (key: string) => string): string {
  if (state === 'idle') return ''
  const keys: Record<Exclude<TenantResultState, 'idle'>, string> = {
    queued: 'aiChat:resultState.queued',
    running: 'aiChat:resultState.running',
    result_processing: 'aiChat:resultState.resultProcessing',
    ready: 'aiChat:resultState.ready',
    result_timeout: 'aiChat:resultState.resultTimeout',
    failed: 'aiChat:resultState.failed',
    cancelled: 'aiChat:resultState.cancelled',
  }
  return t(keys[state])
}

interface SelectedChatAsset {
  localId: string
  assetId: string
  filename: string
  thumbnailUrl: string
}

function conversationHasGeneratedImages(detail: AIChatConversationDetail): boolean {
  // Pre-PC3: missing/unknown asset_role still means “has images” for the
  // history filter (Gemini output_assets P0). Explicit non-generated roles
  // (inline/reference/…) are excluded once PC3 classifies them.
  return detail.messages.some((m) =>
    m.executions.some((e) =>
      (e.output_assets || []).some((a) => {
        const role = a.asset_role || 'unknown'
        return role === 'generated' || role === 'unknown'
      }),
    ),
  )
}

function newLocalId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const THUMBNAIL_RETRY_DELAYS_MS = [400, 1200, 3000]

/**
 * P0-1 (attachment thumbnail invisible): the authenticated `/content` byte
 * delivery this thumbnail depends on can transiently miss on the very
 * first load right after an upload/pick (R2 eventual-consistency window,
 * a cold session cookie, a redirect hiccup) — a bare `<img>` just shows
 * the browser's broken-image icon forever with no recovery. This retries
 * a few times with backoff (cache-busted so a real browser/CDN cache
 * never masks the retry), then falls back to an explicit, safe
 * "加载失败，点击重试" affordance instead of leaving a permanently broken icon.
 */
function ResilientThumbnail(
  props: ImgHTMLAttributes<HTMLImageElement> & { src: string; retryTitle: string; retryLabel: string },
) {
  const { src, alt, retryTitle, retryLabel, ...rest } = props
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setAttempt(0)
    setFailed(false)
  }, [src])

  const resolvedSrc = attempt === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}retry=${attempt}`

  if (failed) {
    return (
      <div
        className="tc-aichat-thumb-broken"
        role="button"
        tabIndex={0}
        title={retryTitle}
        onClick={(e) => {
          e.stopPropagation()
          setFailed(false)
          setAttempt((a) => a + 1)
        }}
      >
        <PictureOutlined />
        <span>{retryLabel}</span>
      </div>
    )
  }

  return (
    <img
      {...rest}
      src={resolvedSrc}
      alt={alt}
      onError={() => {
        if (attempt < THUMBNAIL_RETRY_DELAYS_MS.length) {
          window.setTimeout(() => setAttempt((a) => a + 1), THUMBNAIL_RETRY_DELAYS_MS[attempt])
        } else {
          setFailed(true)
        }
      }}
    />
  )
}

function formatShortTime(iso: string | null, locale: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(d)
}

function formatHistoryTime(
  iso: string | null,
  bucket: AIChatConversationSummary['bucket'],
  locale: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (bucket === 'today') return formatShortTime(iso, locale)
  if (bucket === 'yesterday') return t('aiChat:history.yesterdayTime', { time: formatShortTime(iso, locale) })
  if (!iso) return t('aiChat:history.bucket.earlier')
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return t('aiChat:history.bucket.earlier')
  return formatDateTimeI18n(iso, locale as 'zh-CN' | 'en-US')
}

function providerSortOrder(config: AIChatConfig | null): AIChatProvider[] {
  const fromConfig = config?.providers?.map((p) => p.id) ?? []
  const merged = [...fromConfig]
  for (const p of PROVIDER_ORDER) {
    if (!merged.includes(p)) merged.push(p)
  }
  return merged
}

function collapseKey(messageId: string | null, provider: string): string {
  return `${messageId ?? 'idle'}:${provider}`
}

async function uploadSiteAssetToChatAsset(
  workspaceId: string,
  siteId: string,
  file: File,
): Promise<ImageAssetPickerSelection> {
  const { data: uploaded } = await uploadSiteAsset(workspaceId, siteId, file)
  const { data: resolved } = await resolveImagePickerSelection(workspaceId, {
    picker_item_id: `sa:${uploaded.id}`,
    target: 'ai_chat_asset',
    site_id: siteId,
  })
  return {
    assetId: resolved.asset_id,
    pickerItemId: resolved.picker_item_id,
    referenceType: 'ai_chat_asset',
    filename: uploaded.original_filename || file.name,
  }
}

export default function AIChatPage() {
  const { t } = useTranslation(['aiChat', 'actions', 'status'])
  const { locale } = useUiLocale()
  const { workspaceId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const { primarySite } = useWorkspaceState()
  const siteId = primarySite?.id ?? ''

  const [config, setConfig] = useState<AIChatConfig | null>(null)
  const [conversations, setConversations] = useState<AIChatConversationSummary[]>([])
  const [conversationsLoading, setConversationsLoading] = useState(false)
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [activeConversation, setActiveConversation] = useState<AIChatConversationDetail | null>(null)
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const [prompt, setPrompt] = useState('')
  const [selectedAssets, setSelectedAssets] = useState<SelectedChatAsset[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [addingDroppedFiles, setAddingDroppedFiles] = useState(false)
  const [sending, setSending] = useState(false)
  const [nowMs, setNowMs] = useState(() => Date.now())

  const [collapsedProviders, setCollapsedProviders] = useState<Set<string>>(() => new Set())
  const [workspaceExpanded, setWorkspaceExpanded] = useState(false)
  const [currentInputExpanded, setCurrentInputExpanded] = useState(false)
  const [imageOnlyFilter, setImageOnlyFilter] = useState(false)
  const promptRef = useRef<TextAreaRef>(null)

  const maxImages = config?.max_images_per_message ?? FALLBACK_MAX_IMAGES
  const promptMax = config?.prompt_max_length ?? FALLBACK_PROMPT_MAX
  const sortedProviders = useMemo(() => providerSortOrder(config), [config])
  const providerLabels = useMemo(() => {
    const map: Record<string, string> = { ...PROVIDER_FALLBACK_LABEL }
    map.qwen = t('aiChat:provider.fallbackLabel.qwen')
    map.deepseek = t('aiChat:provider.fallbackLabel.deepseek')
    for (const p of config?.providers ?? []) map[p.id] = p.label
    return map
  }, [config, t])

  useEffect(() => {
    if (!workspaceId) return
    getAIChatConfig(workspaceId)
      .then((r) => setConfig(r.data))
      .catch(() => {})
  }, [workspaceId])

  const loadConversations = useCallback(async () => {
    if (!workspaceId) return
    try {
      const { data } = await listAIChatConversations(workspaceId)
      setConversations(data.items)
    } catch {
      /* keep last-known */
    }
  }, [workspaceId])

  useEffect(() => {
    setConversationsLoading(true)
    loadConversations().finally(() => setConversationsLoading(false))
  }, [loadConversations])

  const loadDetail = useCallback(
    async (conversationId: string, preferredMessageId?: string | null) => {
      if (!workspaceId) return
      try {
        const { data } = await getAIChatConversation(workspaceId, conversationId)
        setActiveConversation(data)
        const hasImages = conversationHasGeneratedImages(data)
        setConversations((prev) =>
          prev.map((c) => (c.id === data.id ? { ...c, has_generated_image: hasImages } : c)),
        )
        const fallback = data.messages.length > 0 ? data.messages[data.messages.length - 1].id : null
        const nextMessageId =
          preferredMessageId && data.messages.some((m) => m.id === preferredMessageId) ? preferredMessageId : fallback
        setActiveMessageId(nextMessageId)
      } catch {
        /* keep last-known */
      }
    },
    [workspaceId],
  )

  const selectConversation = async (conversationId: string) => {
    if (conversationId === activeConversationId && activeConversation) return
    setActiveConversationId(conversationId)
    setActiveConversation(null)
    setActiveMessageId(null)
    setDetailLoading(true)
    await loadDetail(conversationId)
    setDetailLoading(false)
  }

  // VISTOERA_TENANT_OVERVIEW_HOME_V1 §13/§16 — "最近生成任务" AI Chat rows
  // deep-link here as `?conversationId=...` (the only recent-task job type
  // with a real per-item destination, not just its list page). Read once on
  // mount per `workspaceId`; not re-run on every `searchParams` identity
  // change so a user's own subsequent in-page conversation switches are
  // never fought back to the URL's original value.
  useEffect(() => {
    const conversationId = searchParams.get('conversationId')
    if (!conversationId || !workspaceId) return
    void selectConversation(conversationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId])

  const startNewChat = () => {
    setActiveConversationId(null)
    setActiveConversation(null)
    setActiveMessageId(null)
    setSelectedAssets([])
    setPrompt('')
    setCollapsedProviders(new Set())
  }

  const activeMessage = useMemo(() => {
    if (!activeConversation?.messages.length) return null
    if (activeMessageId) {
      return activeConversation.messages.find((m) => m.id === activeMessageId) ?? null
    }
    return activeConversation.messages[activeConversation.messages.length - 1]
  }, [activeConversation, activeMessageId])

  // Collapse the "本次输入" full-prompt panel back to its default height
  // every time the selected history round actually changes (spec §5: "默认
  // 可显示合理高度") — never carries an expanded state over from a
  // previously-viewed, unrelated message.
  useEffect(() => {
    setCurrentInputExpanded(false)
  }, [activeMessage?.id])

  // Result UX Fix P0 (spec §24) — polling must NOT stop the instant
  // `execution.status === "succeeded"`; it has to keep going until the
  // tenant actually has a renderable result (or the propagation timeout
  // gives up on that particular execution — `POLL_HARD_STOP_MS`). This
  // depends on `nowMs`, but that is safe here: it's a derived boolean, not
  // an object, so the interval effect below only ever resubscribes when it
  // actually flips (React's `Object.is` dependency check), never on every
  // 1s tick.
  const hasNonTerminal = useMemo(() => {
    if (!activeConversation) return false
    return activeConversation.messages.some((m) =>
      m.executions.some((e) => {
        if (!TERMINAL_STATUSES.has(e.status)) return true
        if (e.status !== 'succeeded' || hasRenderableResult(e)) return false
        const completedAt = e.completed_at ? new Date(e.completed_at).getTime() : null
        if (completedAt == null || Number.isNaN(completedAt)) return true
        return nowMs - completedAt < POLL_HARD_STOP_MS
      }),
    )
  }, [activeConversation, nowMs])

  useEffect(() => {
    if (!hasNonTerminal || !activeConversationId) return
    const t = window.setInterval(() => loadDetail(activeConversationId, activeMessageId), POLL_MS)
    return () => window.clearInterval(t)
  }, [hasNonTerminal, activeConversationId, activeMessageId, loadDetail])

  // Same "safe to depend on nowMs" reasoning as above — drives both the
  // running-execution elapsed-seconds display and the live
  // processing->timeout transition in `resolveTenantResultState` without
  // waiting for the next network poll.
  useEffect(() => {
    if (!hasNonTerminal) return
    const t = window.setInterval(() => setNowMs(Date.now()), ELAPSED_TICK_MS)
    return () => window.clearInterval(t)
  }, [hasNonTerminal])

  const elapsedSeconds = useCallback(
    (execution: AIChatExecution): number | null => {
      if (execution.status !== 'running' || !execution.started_at) return null
      const started = new Date(execution.started_at).getTime()
      if (Number.isNaN(started)) return null
      return Math.max(0, Math.floor((nowMs - started) / 1000))
    },
    [nowMs],
  )

  const remainingImageSlots = Math.max(0, maxImages - selectedAssets.length)

  const appendResolvedSelections = (selections: ImageAssetPickerSelection[]) => {
    if (selections.length === 0) return
    setSelectedAssets((prev) => {
      const remaining = Math.max(0, maxImages - prev.length)
      const allowed = selections.slice(0, remaining)
      if (selections.length > remaining) message.warning(t('aiChat:toast.maxImages', { max: maxImages }))
      return [
        ...prev,
        ...allowed.map((sel) => ({
          localId: newLocalId(),
          assetId: sel.assetId,
          filename: sel.filename || t('aiChat:thumbnail.defaultFilename'),
          thumbnailUrl: aiChatAssetContentUrl(workspaceId, sel.assetId),
        })),
      ]
    })
  }

  const openImagePicker = () => {
    if (!siteId) {
      message.error(t('aiChat:toast.noSiteForPicker'))
      return
    }
    if (remainingImageSlots <= 0) {
      message.warning(t('aiChat:toast.maxImages', { max: maxImages }))
      return
    }
    setPickerOpen(true)
  }

  const removeSelectedAsset = (localId: string) => {
    setSelectedAssets((prev) => prev.filter((asset) => asset.localId !== localId))
  }

  const addDroppedFiles = async (files: FileList | File[]) => {
    if (!siteId) {
      message.error(t('aiChat:toast.noSiteForUpload'))
      return
    }
    const list = Array.from(files)
    if (list.length > remainingImageSlots) message.warning(t('aiChat:toast.maxImages', { max: maxImages }))
    const allowed = config?.image_allowed_mime
    const maxBytes = (config?.image_max_size_mb ?? 8) * 1024 * 1024
    const toUpload = list.slice(0, remainingImageSlots).filter((file) => {
      if (allowed && allowed.length > 0 && !allowed.includes(file.type)) {
        message.error(t('aiChat:toast.unsupportedFormat', { name: file.name }))
        return false
      }
      if (file.size > maxBytes) {
        message.error(t('aiChat:toast.fileTooLarge', { name: file.name }))
        return false
      }
      return true
    })
    if (toUpload.length === 0) return

    setAddingDroppedFiles(true)
    try {
      for (const file of toUpload) {
        const resolved = await uploadSiteAssetToChatAsset(workspaceId, siteId, file)
        setSelectedAssets((prev) => {
          if (prev.length >= maxImages) return prev
          return [
            ...prev,
            {
              localId: newLocalId(),
              assetId: resolved.assetId,
              filename: resolved.filename || file.name,
              thumbnailUrl: aiChatAssetContentUrl(workspaceId, resolved.assetId),
            },
          ]
        })
      }
    } catch (e) {
      message.error((e as Error).message || t('aiChat:toast.uploadFailed'))
    } finally {
      setAddingDroppedFiles(false)
    }
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(false)
    if (e.dataTransfer.files?.length) void addDroppedFiles(e.dataTransfer.files)
  }

  const promptTrimmed = prompt.trim()
  const canSend = promptTrimmed.length > 0 && !sending && !addingDroppedFiles

  const handleSend = async () => {
    if (!canSend || !workspaceId) return
    setSending(true)
    const clientRequestId = newLocalId()
    const assetIds = selectedAssets.map((asset) => asset.assetId)
    try {
      const { data } = await sendAIChatMessage(workspaceId, {
        conversation_id: activeConversationId,
        text: promptTrimmed,
        asset_ids: assetIds,
        client_request_id: clientRequestId,
      })
      setActiveConversationId(data.conversation_id)
      setActiveMessageId(data.id)
      setActiveConversation((prev) => {
        if (prev && prev.id === data.conversation_id) {
          return { ...prev, messages: [...prev.messages, data] }
        }
        return { id: data.conversation_id, title: t('aiChat:newConversationTitle'), created_at: null, updated_at: null, messages: [data] }
      })
      setPrompt('')
      setSelectedAssets([])
      setCollapsedProviders(new Set())
      loadConversations()
    } catch (e) {
      message.error((e as Error).message || t('aiChat:toast.submitFailed'))
    } finally {
      setSending(false)
    }
  }

  // "重新发送" (spec §2/§27/§29/§30) — ONLY copies the historical turn back
  // into the composer. Deliberately does not touch `activeConversationId`/
  // `activeConversation`/`activeMessageId` at all: the user keeps looking
  // at the old result while editing a new prompt, and nothing is actually
  // submitted until they click "提交" themselves (`handleSend` is a
  // completely separate, explicit action). Always reads `message.text`
  // (the real full column, never a truncated list-summary) and restores
  // canonical `asset_id` references — never re-uploads/duplicates the
  // original attachment bytes.
  const handleResend = (targetMessage: AIChatMessage | null) => {
    if (!targetMessage) return
    setPrompt(targetMessage.text)
    const assets = targetMessage.assets ?? []
    // `AIChatMessageAsset` rows CASCADE-delete alongside their
    // `AIChatAsset` (spec §30: "附件不可用" must never silently vanish) —
    // a contiguous 0..N-1 ordinal run is what "all attachments still
    // resolve" looks like; any gap means at least one was dropped.
    const maxOrdinal = assets.reduce((max, a) => Math.max(max, a.ordinal), -1)
    const expectedCount = maxOrdinal + 1
    if (expectedCount > assets.length) {
      message.warning(t('aiChat:toast.partialAttachments', { count: expectedCount - assets.length }))
    }
    setSelectedAssets(
      assets.map((a) => ({
        localId: newLocalId(),
        assetId: a.asset_id,
        filename: a.original_filename || t('aiChat:thumbnail.defaultFilename'),
        thumbnailUrl: aiChatAssetContentUrl(workspaceId, a.asset_id),
      })),
    )
    // Focus without submitting — the user still has to click "提交"
    // themselves (spec: "绝对不能直接创建任务").
    window.setTimeout(() => promptRef.current?.focus(), 0)
    message.success(t('aiChat:toast.resendFilled'))
  }

  const handleRetryExecution = async (execution: AIChatExecution) => {
    try {
      const { data: newExecution } = await retryAIChatExecution(workspaceId, execution.id)
      setActiveConversation((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          messages: prev.messages.map((m) =>
            m.id !== execution.message_id
              ? m
              : { ...m, executions: m.executions.map((e) => (e.provider === newExecution.provider ? newExecution : e)) },
          ),
        }
      })
    } catch (e) {
      message.error((e as Error).message || t('aiChat:toast.retryFailed'))
    }
  }

  const handleCopy = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => message.success(t('aiChat:toast.copySuccess')))
      .catch(() => message.error(t('aiChat:toast.copyFailed')))
  }

  const deleteConversation = async (conv: AIChatConversationSummary) => {
    try {
      await deleteAIChatConversation(workspaceId, conv.id)
      setConversations((prev) => prev.filter((c) => c.id !== conv.id))
      if (activeConversationId === conv.id) {
        setActiveConversationId(null)
        setActiveConversation(null)
        setActiveMessageId(null)
      }
      message.success(t('aiChat:toast.deleteSuccess'))
    } catch (e) {
      message.error((e as Error).message || t('aiChat:toast.deleteFailed'))
      throw e
    }
  }

  const toggleProviderCollapsed = (provider: string) => {
    const key = collapseKey(activeMessage?.id ?? null, provider)
    setCollapsedProviders((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const expandAllProviders = () => {
    if (!activeMessage) return
    setCollapsedProviders((prev) => {
      const next = new Set(prev)
      for (const p of sortedProviders) next.delete(collapseKey(activeMessage.id, p))
      return next
    })
  }

  const providerCards = useMemo(() => {
    return sortedProviders.map((provider) => {
      const execution = activeMessage?.executions.find((e) => e.provider === provider)
      const label = execution?.provider_label || providerLabels[provider] || provider
      return { provider, label, execution }
    })
  }, [sortedProviders, activeMessage, providerLabels])

  const groups: { key: AIChatConversationSummary['bucket']; label: string }[] = [
    { key: 'today', label: t('aiChat:history.bucket.today') },
    { key: 'yesterday', label: t('aiChat:history.bucket.yesterday') },
    { key: 'earlier', label: t('aiChat:history.bucket.earlier') },
  ]

  const thumbRetryTitle = t('aiChat:thumbnail.retryTitle')
  const thumbRetryLabel = t('aiChat:thumbnail.retryLabel')

  const filteredConversations = useMemo(() => {
    if (!imageOnlyFilter) return conversations
    return conversations.filter((c) => c.has_generated_image)
  }, [conversations, imageOnlyFilter])

  const messageRounds = activeConversation?.messages ?? []

  return (
    <div className={`tc-aichat-page${workspaceExpanded ? ' is-workspace-expanded' : ''}`}>
      <div className="tc-aichat-left-panel">
        <div className="tc-aichat-composer">
          <div className="tc-aichat-composer-head">
            <div className="tc-aichat-section-title">{t('aiChat:composer.title')}</div>
            <Button type="link" size="small" className="tc-aichat-newchat-link" onClick={startNewChat}>
              {t('aiChat:composer.newChat')}
            </Button>
          </div>
          <Input.TextArea
            ref={promptRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.slice(0, promptMax))}
            placeholder={t('aiChat:composer.placeholder')}
            className="tc-aichat-prompt-textarea"
            autoSize={{ minRows: 5, maxRows: 8 }}
          />
          <div className="tc-aichat-prompt-hint">
            <span>{t('aiChat:composer.charCount', { current: prompt.length, max: promptMax })}</span>
            <span>{t('aiChat:composer.hint')}</span>
          </div>

          <div className="tc-aichat-upload-label">{t('aiChat:composer.uploadLabel', { max: maxImages })}</div>
          <div
            className={`tc-aichat-upload-zone${dragActive ? ' is-dragging' : ''}${selectedAssets.length >= maxImages ? ' is-full' : ''}`}
            role="button"
            tabIndex={0}
            onClick={openImagePicker}
            onDragOver={(e) => {
              e.preventDefault()
              if (selectedAssets.length < maxImages) setDragActive(true)
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
          >
            {selectedAssets.length === 0 ? (
              <>
                <PictureOutlined className="tc-aichat-upload-icon" />
                <div>{addingDroppedFiles ? t('aiChat:composer.uploading') : t('aiChat:composer.uploadIdle')}</div>
              </>
            ) : (
              <div className="tc-aichat-upload-thumbs">
                {selectedAssets.map((asset) => (
                  <div key={asset.localId} className="tc-aichat-thumb">
                    <ResilientThumbnail src={asset.thumbnailUrl} alt={asset.filename} retryTitle={thumbRetryTitle} retryLabel={thumbRetryLabel} />
                    <Button
                      size="small"
                      type="text"
                      className="tc-aichat-thumb-remove"
                      icon={<CloseCircleOutlined />}
                      onClick={(e) => {
                        e.stopPropagation()
                        removeSelectedAsset(asset.localId)
                      }}
                    />
                  </div>
                ))}
                {selectedAssets.length < maxImages && (
                  <div
                    className="tc-aichat-thumb tc-aichat-thumb-add"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation()
                      openImagePicker()
                    }}
                  >
                    {addingDroppedFiles ? <LoadingOutlined /> : <PlusOutlined />}
                  </div>
                )}
              </div>
            )}
          </div>

          <Button type="primary" block loading={sending} disabled={!canSend} onClick={handleSend} className="tc-aichat-submit-btn">
            {t('actions:submit')}
          </Button>
        </div>

        <div className="tc-aichat-history">
          <div className="tc-aichat-history-head">
            <span>{t('aiChat:history.title')}</span>
            <Button
              type="text"
              size="small"
              className={`tc-aichat-image-filter${imageOnlyFilter ? ' is-active' : ''}`}
              onClick={() => setImageOnlyFilter((v) => !v)}
            >
              {imageOnlyFilter ? t('aiChat:history.imageFilterOn') : t('aiChat:history.imageFilterOff')}
            </Button>
          </div>
          <div className="tc-aichat-history-scroll">
            {groups.map((g) => {
              const items = filteredConversations.filter((c) => c.bucket === g.key)
              if (items.length === 0) return null
              return (
                <div key={g.key}>
                  <div className="tc-aichat-history-group-label">{g.label}</div>
                  {items.map((c) => (
                    <div
                      key={c.id}
                      className={`tc-aichat-history-item${c.id === activeConversationId ? ' is-active' : ''}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => void selectConversation(c.id)}
                    >
                      <div className="tc-aichat-history-item-main">
                        <div className="tc-aichat-history-item-title" title={c.title}>
                          {c.has_generated_image && (
                            <FileImageOutlined className="tc-aichat-history-image-icon" aria-hidden />
                          )}
                          <span className="tc-aichat-history-item-title-text">{c.title}</span>
                        </div>
                        <div className="tc-aichat-history-item-time">{formatHistoryTime(c.updated_at, c.bucket, locale, t)}</div>
                      </div>
                      <span onClick={(e) => e.stopPropagation()}>
                        <VistoeraActionConfirm
                          title={t('aiChat:history.deleteConfirm.title')}
                          description={t('aiChat:history.deleteConfirm.description')}
                          confirmLabel={t('actions:delete')}
                          tooltip={t('actions:delete')}
                          onConfirm={() => deleteConversation(c)}
                        >
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            className="tc-aichat-history-item-delete"
                          />
                        </VistoeraActionConfirm>
                      </span>
                    </div>
                  ))}
                </div>
              )
            })}
            {!conversationsLoading && filteredConversations.length === 0 && (
              <div className="tc-aichat-history-empty">
                {imageOnlyFilter ? t('aiChat:history.emptyImageFilter') : t('aiChat:history.empty')}
              </div>
            )}

            {messageRounds.length > 1 && (
              <div className="tc-aichat-rounds">
                <div className="tc-aichat-rounds-label">{t('aiChat:history.roundsLabel')}</div>
                {messageRounds.map((m) => (
                  <div
                    key={m.id}
                    className={`tc-aichat-round-item${m.id === activeMessage?.id ? ' is-active' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveMessageId(m.id)}
                  >
                    <div className="tc-aichat-round-item-text" title={m.text}>
                      {m.text}
                    </div>
                    <div className="tc-aichat-round-item-time">{formatShortTime(m.created_at, locale)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {siteId && (
        <ImageAssetPicker
          open={pickerOpen}
          workspaceId={workspaceId}
          siteId={siteId}
          purpose="ai_chat_input"
          title={t('aiChat:picker.title')}
          selectionMode="multiple"
          maxSelection={remainingImageSlots}
          onCancel={() => setPickerOpen(false)}
          onConfirm={(selections) => {
            setPickerOpen(false)
            appendResolvedSelections(selections)
          }}
        />
      )}

      <div className="tc-aichat-results-workspace">
        <div className="tc-aichat-results-toolbar">
          <Button size="small" onClick={expandAllProviders} disabled={!activeMessage}>
            {t('aiChat:workspace.expandAll')}
          </Button>
          <Button
            size="small"
            icon={workspaceExpanded ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setWorkspaceExpanded((v) => !v)}
          >
            {workspaceExpanded ? t('aiChat:workspace.exitFullscreen') : t('aiChat:workspace.fullscreen')}
          </Button>
        </div>

        {activeMessage && (
          <div className="tc-aichat-current-input">
            <div className="tc-aichat-current-input-head">
              <span className="tc-aichat-section-title">{t('aiChat:workspace.currentInput')}</span>
              <Button size="small" icon={<RedoOutlined />} onClick={() => handleResend(activeMessage)}>
                {t('aiChat:workspace.resend')}
              </Button>
            </div>
            {activeMessage.assets.length > 0 && (
              <div className="tc-aichat-current-input-images">
                {activeMessage.assets.map((a) => (
                  <ResilientThumbnail
                    key={a.asset_id}
                    src={aiChatAssetContentUrl(workspaceId, a.asset_id)}
                    alt={a.original_filename || t('aiChat:thumbnail.defaultFilename')}
                    loading="lazy"
                    retryTitle={thumbRetryTitle}
                    retryLabel={thumbRetryLabel}
                  />
                ))}
              </div>
            )}
            <div className={`tc-aichat-current-input-text${currentInputExpanded ? ' is-expanded' : ''}`}>
              {activeMessage.text}
            </div>
            {(activeMessage.text.length > 160 || activeMessage.text.split('\n').length > 4) && (
              <button
                type="button"
                className="tc-aichat-current-input-toggle"
                onClick={() => setCurrentInputExpanded((v) => !v)}
              >
                {currentInputExpanded ? t('aiChat:workspace.collapse') : t('aiChat:workspace.expand')}
              </button>
            )}
          </div>
        )}

        <div className="tc-aichat-results-body">
          {detailLoading ? (
            <div className="tc-aichat-loading">{t('aiChat:workspace.loading')}</div>
          ) : (
            <div className="tc-aichat-provider-row">
              {providerCards.map(({ provider, label, execution }) => {
                const collapsed = collapsedProviders.has(collapseKey(activeMessage?.id ?? null, provider))
                return (
                  <ProviderCard
                    key={provider}
                    provider={provider}
                    label={label}
                    workspaceId={workspaceId}
                    execution={execution}
                    collapsed={collapsed}
                    elapsedSeconds={execution ? elapsedSeconds(execution) : null}
                    nowMs={nowMs}
                    onToggleCollapse={() => toggleProviderCollapsed(provider)}
                    onRetry={handleRetryExecution}
                    onCopy={handleCopy}
                    onRefresh={() => activeConversationId && loadDetail(activeConversationId, activeMessageId)}
                    idle={!activeMessage}
                  />
                )
              })}
            </div>
          )}
        </div>

        <div className="tc-aichat-results-footer-note">{t('aiChat:workspace.disclaimer')}</div>
      </div>
    </div>
  )
}

function assetPreviewUrl(workspaceId: string, asset: AIChatOutputAsset, variant: 'thumbnail' | 'web' | 'original' = 'web'): string {
  if (variant === 'thumbnail' && asset.thumbnail_url) return asset.thumbnail_url
  if (variant === 'original' && asset.download_url) return asset.download_url
  return siteAssetVariantUrl(workspaceId, asset.site_asset_id, variant === 'original' ? 'original' : variant)
}

function OutputImageGallery({ workspaceId, assets }: { workspaceId: string; assets: AIChatOutputAsset[] }) {
  const { t } = useTranslation(['aiChat', 'actions'])
  const navigate = useNavigate()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [previewOpen, setPreviewOpen] = useState(false)
  const safeIndex = Math.min(selectedIndex, Math.max(0, assets.length - 1))
  const selected = assets[safeIndex]

  useEffect(() => {
    setSelectedIndex(0)
  }, [assets])

  useEffect(() => {
    if (previewOpen) {
      lockPageScroll()
      return () => unlockPageScroll()
    }
    return undefined
  }, [previewOpen])

  if (!selected) return null

  const mainUrl = assetPreviewUrl(workspaceId, selected, 'web')
  // "查看大图" (spec §12) — plain durable asset URL, inline-viewable (Ant
  // Design `<Image>` preview / a new tab), NEVER the forced-download one.
  const viewLargeUrl = assetPreviewUrl(workspaceId, selected, 'original')
  // "下载" (spec §11-§15) — same canonical `variants/original` route, just
  // with `?download=true` so the SaaS Asset Runtime backend answers with
  // `Content-Disposition: attachment` (see `saas_assets.py::get_variant`).
  // A plain `<a href>` click (no `target="_blank"`) against a response
  // carrying that header always triggers a native "Save As" instead of a
  // new-tab navigation — that header, not a frontend `download`
  // HTML attribute (silently ignored cross-origin), is what actually fixes
  // "下载" opening a new tab. Filename is intentionally left for the
  // server's own `Content-Disposition` (canonical asset name), never
  // invented client-side.
  const forcedDownloadUrl = `${viewLargeUrl}${viewLargeUrl.includes('?') ? '&' : '?'}download=true`

  const triggerDownload = () => {
    triggerBrowserDownload(forcedDownloadUrl)
  }

  const goGenerateVideo = () => {
    const pickerItemId = selected.picker_item_id || `sa:${selected.site_asset_id}`
    navigate(
      `/workspace/${workspaceId}/video-generation?reference_image_picker_item_id=${encodeURIComponent(pickerItemId)}`,
    )
  }

  return (
    <div className="tc-aichat-output-gallery">
      <div className="tc-aichat-output-main">
        {/* P0-5: single-click keeps existing (non-destructive, no-op) behavior;
            double-click reuses the exact same large-image viewer as "查看大图"
            (never a second modal) — background scroll lock/restore already
            covered by the `previewOpen` effect above. */}
        <img
          src={mainUrl}
          alt={t('aiChat:output.generatedImageAlt', { index: safeIndex + 1 })}
          loading="lazy"
          className="tc-aichat-output-main-img"
          onDoubleClick={() => setPreviewOpen(true)}
        />
      </div>
      <div className="tc-aichat-output-actions">
        <Button size="small" type="text" icon={<ZoomInOutlined />} onClick={() => setPreviewOpen(true)}>
          {t('aiChat:output.viewLarge')}
        </Button>
        <Button size="small" type="text" icon={<DownloadOutlined />} onClick={triggerDownload}>
          {t('actions:download')}
        </Button>
        <Button
          size="small"
          type="primary"
          className="tc-aichat-output-video-btn"
          icon={<VideoCameraOutlined />}
          onClick={goGenerateVideo}
        >
          {t('aiChat:output.generateVideo')}
        </Button>
        {assets.length > 1 && (
          <span className="tc-aichat-output-counter">
            {t('aiChat:output.imageCounter', { current: safeIndex + 1, total: assets.length })}
          </span>
        )}
      </div>
      {assets.length > 1 && (
        <div className="tc-aichat-output-thumbs">
          {assets.map((asset, idx) => (
            <button
              key={`${asset.site_asset_id}-${asset.ordinal}`}
              type="button"
              className={`tc-aichat-output-thumb${idx === safeIndex ? ' is-selected' : ''}`}
              onClick={() => setSelectedIndex(idx)}
            >
              <img src={assetPreviewUrl(workspaceId, asset, 'thumbnail')} alt={t('aiChat:output.thumbnailAlt', { index: idx + 1 })} loading="lazy" />
            </button>
          ))}
        </div>
      )}
      <Image
        style={{ display: 'none' }}
        preview={{
          visible: previewOpen,
          src: viewLargeUrl,
          onVisibleChange: (visible) => setPreviewOpen(visible),
        }}
      />
    </div>
  )
}

function ProviderCard({
  provider,
  label,
  workspaceId,
  execution,
  collapsed,
  elapsedSeconds,
  nowMs,
  onToggleCollapse,
  onRetry,
  onCopy,
  onRefresh,
  idle,
}: {
  provider: AIChatProvider
  label: string
  workspaceId: string
  execution: AIChatExecution | undefined
  collapsed: boolean
  elapsedSeconds: number | null
  nowMs: number
  onToggleCollapse: () => void
  onRetry: (execution: AIChatExecution) => void
  onCopy: (text: string) => void
  onRefresh: () => void
  idle: boolean
}) {
  const { t } = useTranslation(['aiChat', 'actions'])
  const responseText = (execution?.response_text || '').trim()
  const outputAssets = execution?.output_assets ?? []
  const rich = execution?.rich_response as RichResponseV1 | null | undefined
  const useRich = hasRichResponse(rich)
  const hasText = responseText.length > 0
  const hasImages = outputAssets.length > 0
  // Tenant Result Readiness (spec §17-§26) — never renders directly off
  // `execution.status`; every branch below reads this one derived state so
  // "已完成" can only ever appear once there is something real to show.
  const resultState = resolveTenantResultState(execution, nowMs)

  return (
    <div
      className={`tc-aichat-provider-card${collapsed ? ' is-collapsed' : ''}`}
      onClick={collapsed ? onToggleCollapse : undefined}
      role={collapsed ? 'button' : undefined}
      tabIndex={collapsed ? 0 : undefined}
      onKeyDown={
        collapsed
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggleCollapse()
              }
            }
          : undefined
      }
    >
      <div className="tc-aichat-provider-header">
        <Avatar size={26} style={{ backgroundColor: PROVIDER_AVATAR_COLOR[provider] ?? '#64748b' }}>
          {label.slice(0, 1)}
        </Avatar>
        <span className="tc-aichat-provider-name">{label}</span>
        {resultState !== 'idle' && !collapsed && (
          <span className={`tc-badge ${RESULT_STATE_BADGE_CLASS[resultState]}`}>{resultStateLabel(resultState, t)}</span>
        )}
        {!collapsed && (
          <Button
            size="small"
            type="text"
            className="tc-aichat-provider-close"
            icon={<CloseOutlined />}
            aria-label={t('aiChat:provider.collapseAria', { label })}
            onClick={(e) => {
              e.stopPropagation()
              onToggleCollapse()
            }}
          />
        )}
        <div className="tc-aichat-provider-collapsed-label">
          <Avatar size={22} style={{ backgroundColor: PROVIDER_AVATAR_COLOR[provider] ?? '#64748b' }}>
            {label.slice(0, 1)}
          </Avatar>
          <span>{label}</span>
        </div>
      </div>

      {!collapsed && (
        <>
          <div className="tc-aichat-provider-body">
            {idle && resultState === 'idle' && <span className="tc-aichat-muted">{t('aiChat:provider.idle')}</span>}
            {!idle && resultState === 'idle' && <span className="tc-aichat-muted">{t('aiChat:provider.waiting')}</span>}
            {resultState === 'queued' && <span className="tc-aichat-muted">{t('aiChat:provider.queued')}</span>}
            {resultState === 'running' && (
              <span className="tc-aichat-muted">
                <LoadingOutlined /> {t('aiChat:provider.running')}
                {elapsedSeconds != null ? t('aiChat:provider.runningElapsed', { seconds: elapsedSeconds }) : ''}
              </span>
            )}
            {resultState === 'result_processing' && (
              <span className="tc-aichat-muted">
                <LoadingOutlined /> {t('aiChat:provider.resultProcessing')}
              </span>
            )}
            {isResultReadyState(resultState) && (
              <>
                {useRich && rich ? (
                  <RichResponseRenderer workspaceId={workspaceId} richResponse={rich} assets={outputAssets} />
                ) : (
                  <>
                    {hasText && (
                      <div className="tc-aichat-markdown">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{execution?.response_text || ''}</ReactMarkdown>
                      </div>
                    )}
                    {hasImages && <OutputImageGallery workspaceId={workspaceId} assets={outputAssets} />}
                  </>
                )}
              </>
            )}
            {resultState === 'result_timeout' && (
              <div className="tc-aichat-error">
                <div className="tc-aichat-error-title">{t('aiChat:provider.resultTimeoutTitle')}</div>
                <div className="tc-aichat-error-detail">{t('aiChat:provider.resultTimeoutDetail')}</div>
                <Button size="small" onClick={onRefresh} className="tc-aichat-result-timeout-refresh">
                  {t('actions:refresh')}
                </Button>
              </div>
            )}
            {resultState === 'failed' && (
              <div className="tc-aichat-error">
                <div className="tc-aichat-error-title">{t('aiChat:provider.failedTitle')}</div>
                <div className="tc-aichat-error-detail">{execution?.error_message || t('aiChat:provider.unknownError')}</div>
              </div>
            )}
            {resultState === 'cancelled' && <span className="tc-aichat-muted">{t('aiChat:resultState.cancelled')}</span>}
          </div>
          {execution && (isResultReadyState(resultState) || resultState === 'result_timeout' || resultState === 'failed') && (
            <div className="tc-aichat-provider-footer">
              {isResultReadyState(resultState) && hasText && (
                <Button size="small" type="text" icon={<CopyOutlined />} onClick={() => onCopy(execution.response_text || '')}>
                  {t('actions:copy')}
                </Button>
              )}
              <Button size="small" type="text" icon={<RedoOutlined />} onClick={() => onRetry(execution)}>
                {t('aiChat:provider.regenerate')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
